<template>
  <div class="flex flex-col h-full" data-private>
    <div class="flex items-start justify-between px-4 pt-4 pb-2">
      <div>
        <h2 class="text-lg font-semibold text-gray-800">EHR Agentic Parse</h2>
        <p class="text-xs text-gray-500 mt-1 max-w-3xl">
          Open care gaps where supporting evidence was found in the practice's EHR feed
          (encounter-note text and structured vitals) and validated against the HEDIS
          MY2026 specification. Every row is a gap the payer still reports
          <span class="font-medium text-gray-700">Non-Compliant</span>.
        </p>
        <div v-if="practices.length" class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span
            v-for="p in practices" :key="p.TIN"
            class="text-xs text-gray-600"
          >
            <span class="font-semibold text-gray-800">{{ p.PRACTICE_NAME }}</span>
            <span class="text-gray-400"> · TIN {{ p.TIN }} · EHR feed {{ p.EK_CLIENT_ID }} · </span>
            <span class="font-medium text-gray-700">{{ p.CLOSURES }}</span> closures /
            <span class="font-medium text-gray-700">{{ p.PATIENTS }}</span> patients
          </span>
        </div>
      </div>
      <div v-if="total" class="flex items-center gap-4 text-xs text-gray-500 pt-1 shrink-0">
        <span><span class="font-semibold text-gray-700">{{ total }}</span> closures</span>
        <span><span class="font-semibold text-gray-700">{{ patients }}</span> patients</span>
        <button
          @click="exportCsv"
          class="border border-gray-200 rounded px-2 py-1 hover:bg-gray-50 text-gray-600"
        >Export CSV</button>
      </div>
    </div>

    <div v-if="practices.length > 1" class="px-4 pb-2 flex items-center gap-1.5">
      <span class="text-xs text-gray-500 font-medium">Practice</span>
      <select
        v-model="filterPractice"
        class="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white min-w-[14rem]"
      >
        <option :value="''">All practices</option>
        <option v-for="p in practices" :key="p.TIN" :value="p.PRACTICE_NAME">
          {{ p.PRACTICE_NAME }} ({{ p.CLOSURES }})
        </option>
      </select>
    </div>

    <div v-if="byMeasure.length" class="px-4 pb-3 flex flex-wrap gap-2">
      <button
        v-for="m in byMeasure" :key="m.MEASURE_CODE"
        @click="filterMeasure = filterMeasure === m.MEASURE_CODE ? '' : m.MEASURE_CODE"
        class="text-xs rounded border px-2.5 py-1 transition-colors"
        :class="filterMeasure === m.MEASURE_CODE
          ? 'border-primary-500 bg-primary-50 text-gray-900 font-medium'
          : 'border-gray-200 text-gray-600 hover:bg-gray-50'"
      >
        {{ m.MEASURE_CODE }}
        <span class="ml-1 font-semibold text-gray-800">{{ m.CLOSURES }}</span>
      </button>
    </div>

    <div v-if="loading" class="px-4 py-6 text-sm text-gray-500">
      Loading extracted gap closures…
    </div>
    <div v-else-if="error" class="px-4 py-6 text-sm text-red-600">{{ error }}</div>
    <div v-else-if="!rows.length" class="px-4 py-6 text-sm text-gray-500">
      No extracted gap closures available.
    </div>

    <div v-else class="flex-1 min-h-0 flex gap-0">
      <div
        class="min-h-0 overflow-auto px-4 pb-4 transition-all duration-300"
        :style="{ width: selectedPatient ? '50%' : '100%' }"
      >
      <table class="w-full text-xs border-collapse">
        <thead class="sticky top-0 bg-gray-50">
          <tr class="text-left text-gray-600">
            <th class="px-3 py-2 font-medium border-b border-gray-200">Practice</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">Contract</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">Gap</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">Measure</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">Patient</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">MBI</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">Age / Sex</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">Evidence Date</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200">Match</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200 min-w-[15rem]">Source Evidence (EHR)</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200 min-w-[18rem]">Exact Evidence — Verbatim EHR Quote</th>
            <th class="px-3 py-2 font-medium border-b border-gray-200 min-w-[22rem]">Qualifying Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(r, i) in filteredRows" :key="i"
            class="align-top"
            :class="selectedPatient && selectedPatient.PATIENT_IDENTIFIER === r.PATIENT_IDENTIFIER
              ? 'bg-blue-50' : 'hover:bg-gray-50'"
          >
            <td class="px-3 py-2 border-b border-gray-100 text-gray-700">
              {{ r.PRACTICE_NAME }}
              <div class="text-gray-400">TIN {{ r.TIN }}</div>
            </td>
            <td class="px-3 py-2 border-b border-gray-100 text-gray-700">
              {{ r.CONTRACT_NAME }}
            </td>
            <td class="px-3 py-2 border-b border-gray-100 text-gray-700">
              <div v-if="r.GAP_STATUS" class="text-amber-700">
                Gap {{ r.GAP_STATUS }}<span v-if="r.GAP_VALID_FROM"> since {{ String(r.GAP_VALID_FROM).slice(0, 10) }}</span>
              </div>
            </td>
            <td class="px-3 py-2 border-b border-gray-100">
              <span class="inline-block rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-700">
                {{ r.MEASURE_CODE }}
              </span>
            </td>
            <td class="px-3 py-2 border-b border-gray-100">
              <button
                type="button"
                class="text-primary-600 hover:text-primary-700 hover:underline font-medium text-left"
                title="Open this patient's care gaps"
                @click="openPatient(r)"
              >{{ r.PATIENT_NAME }}</button>
            </td>
            <td class="px-3 py-2 border-b border-gray-100">
              <button
                type="button"
                class="font-mono text-primary-600 hover:text-primary-700 hover:underline"
                title="Open this patient's care gaps"
                @click="openPatient(r)"
              >{{ r.PATIENT_IDENTIFIER }}</button>
            </td>
            <td class="px-3 py-2 border-b border-gray-100 text-gray-600">
              {{ r.PATIENT_AGE }}{{ sexLabel(r.PATIENT_SEX) }}
            </td>
            <td class="px-3 py-2 border-b border-gray-100 text-gray-800">{{ r.EVIDENCE_DATE }}</td>
            <td class="px-3 py-2 border-b border-gray-100 text-gray-500">
              {{ r.MATCH_METHOD }}
              <div v-if="r.LLM_CONFIDENCE" class="text-gray-400">conf: {{ r.LLM_CONFIDENCE }}</div>
            </td>
            <td class="px-3 py-2 border-b border-gray-100 text-gray-700">{{ r.SOURCE_NOTE_TEXT }}</td>
            <td class="px-3 py-2 border-b border-gray-100">
              <span class="font-mono text-gray-900 bg-yellow-50 border-l-2 border-yellow-400 pl-2 block py-0.5">
                &ldquo;{{ r.EXACT_EVIDENCE_QUOTE }}&rdquo;
              </span>
            </td>
            <td class="px-3 py-2 border-b border-gray-100 text-gray-600">
              <div class="font-medium text-gray-800">{{ r.QUALIFYING_RULE_SHORT }}</div>
              <div class="mt-1 leading-relaxed">{{ r.QUALIFYING_RULE_DETAIL }}</div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <transition
        enter-active-class="transition-all duration-300"
        leave-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-x-4"
        leave-to-class="opacity-0 translate-x-4"
      >
        <div
          v-if="selectedPatient"
          class="flex flex-col overflow-hidden border-l border-gray-200"
          style="width: 50%"
        >
          <Patient_Drawer
            :key="selectedPatient.PATIENT_IDENTIFIER"
            :patient-identifier="selectedPatient.PATIENT_IDENTIFIER"
            :patient-name="selectedPatient.PATIENT_NAME"
            :enabled-tabs="['details', 'utilization', 'gaps', 'engagement', 'care_team']"
            :default-tab="'gaps'"
            :show-header="true"
            @close="selectedPatient = null"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';

export default {
  name: 'Quality_AgenticParse',
  components: { Patient_Drawer },
  data() {
    return {
      rows: [],
      byMeasure: [],
      practices: [],
      total: 0,
      patients: 0,
      loading: false,
      error: '',
      filterMeasure: '',
      filterPractice: '',
      selectedPatient: null,
    };
  },
  computed: {
    filteredRows() {
      return this.rows.filter((r) => (
        (!this.filterMeasure || r.MEASURE_CODE === this.filterMeasure)
        && (!this.filterPractice || r.PRACTICE_NAME === this.filterPractice)
      ));
    },
  },
  created() {
    this.load();
  },
  methods: {
    openPatient(r) {
      // Open the shared patient drawer in place, on its Care Gaps tab, so the
      // actual open gap sits alongside the extracted evidence.
      this.selectedPatient = (this.selectedPatient
        && this.selectedPatient.PATIENT_IDENTIFIER === r.PATIENT_IDENTIFIER) ? null : r;
    },
    sexLabel(s) {
      const v = String(s || '').trim();
      if (v === '2' || v === '2.0' || v === 'F') return ' / F';
      if (v === '1' || v === '1.0' || v === 'M') return ' / M';
      return '';
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await this.$axios.get('/api/qrda-facts/agentic-parse/');
        this.rows = data.rows || [];
        this.byMeasure = data.byMeasure || [];
        this.practices = data.practices || [];
        this.total = data.total || 0;
        this.patients = data.patients || 0;
      } catch (e) {
        this.error = 'Unable to load extracted gap closures.';
      } finally {
        this.loading = false;
      }
    },
    exportCsv() {
      const cols = ['PRACTICE_NAME', 'TIN', 'EK_CLIENT_ID', 'CONTRACT_NAME',
        'GAP_STATUS', 'GAP_VALID_FROM', 'MEASURE_CODE', 'MEASURE_NAME',
        'PATIENT_NAME', 'PATIENT_IDENTIFIER', 'PATIENT_AGE', 'PATIENT_SEX',
        'MATCH_METHOD', 'EMPI_PATIENT_MASTER', 'EVIDENCE_SOURCE', 'EVIDENCE_DATE',
        'SERVICE_FOUND', 'COMPLETION_STATUS', 'LLM_CONFIDENCE', 'SOURCE_NOTE_TEXT',
        'EXACT_EVIDENCE_QUOTE', 'QUALIFYING_RULE_SHORT', 'QUALIFYING_RULE_DETAIL'];
      const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
      const lines = [cols.join(',')].concat(
        this.filteredRows.map((r) => cols.map((c) => esc(r[c])).join(',')),
      );
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ehr_agentic_parse_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
  },
};
</script>
